import React from 'react';
import { Check } from 'lucide-react';
import type { TopUpPack } from '../../services/walletService';

export interface TopUpPacksProps {
  packs: TopUpPack[];
  /** Extra bonus fraction from an active promo (0 when none is running). */
  promoBonus: number;
  /** Pack id currently being checked out, or '' when idle. */
  buying: string;
  onTopUp: (packId: string) => void;
}

/**
 * The one top-up ladder — four widening lump sums, so a creator picks an amount and stops pricing
 * every click. Presentational: packs, promo bonus and the busy state all arrive as props.
 */
const TopUpPacks: React.FC<TopUpPacksProps> = ({ packs, promoBonus, buying, onTopUp }) => {
  if (!packs.length) {
    return <p className="text-sm text-secondary-400" data-testid="packs-empty">Top-up options are loading…</p>;
  }
  const withPromo = (credits: number) => Math.round(credits * (1 + promoBonus));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {packs.map((p) => (
        <div key={p.id} data-testid={`pack-${p.id}`}
          className={`relative rounded-2xl border p-4 flex flex-col ${p.tag === 'Best value' ? 'border-secondary-900 dark:border-white ring-1 ring-secondary-900 dark:ring-white' : 'border-secondary-200 dark:border-secondary-700'}`}>
          {p.tag && (
            <span className="absolute -top-2 left-4 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-secondary-900 dark:bg-white text-white dark:text-secondary-900">{p.tag}</span>
          )}
          <p className="text-2xl font-bold">${p.price_usd}</p>
          <p className="text-sm text-secondary-600 dark:text-secondary-300">{withPromo(p.credits).toLocaleString()} WP</p>
          <p className="mt-0.5 text-xs text-secondary-500 inline-flex items-center gap-1 min-h-[1rem]">
            {(p.bonus_pct > 0 || promoBonus > 0) && (<><Check className="w-3 h-3" /> +{p.bonus_pct + Math.round(promoBonus * 100)}% bonus</>)}
          </p>
          <button onClick={() => onTopUp(p.id)} disabled={!!buying}
            className="mt-auto px-3 py-2 rounded-xl bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 text-sm font-medium hover:bg-black dark:hover:bg-secondary-100 transition-colors disabled:opacity-50">
            {buying === p.id ? 'Starting…' : 'Top up'}
          </button>
        </div>
      ))}
    </div>
  );
};

export default TopUpPacks;
